import { useState } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import UKImg from "@/common-components-src/components/UKImg";
import JDIPrice from "@/common-components-src/js/price/index";
import { addCartUtil } from "@/app/csr-src/utils/cartUtils";
import styles from "./index.module.scss";
import { OrderStatusVo, DetailWareInfoListVo, TagContentVo } from "@/app/csr-src/apis/order/order.interface";
import { jumpToDp } from "../../utils/jumpMethod";

// 组件
import CommonButton from "../commonButton";
import ReturnProductModal from "../returnProductModal";
import ReturnRequestModal from "../returnRequestModal";
import ApplySuccessModal from "../applySuccessModal";
import ProductTag from "../productTag";
import AgeTipsModal from "../ageTipsModal";
// 图标
import TipIcon from "@/app/csr-src/assets/icons/TipIcon.svg";
// 埋点
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25344";

interface orderDetailProductProps {
  orderInfo: {
    orderStatusDTO: OrderStatusVo;
    wareInfoDTOList: DetailWareInfoListVo[];
    orderId: string;
    postCode: string;
    orderTagContentVO?: TagContentVo;
  };
  handleInitCartNum?: () => void;
  onRefresh?: () => void;
}

const orderDetailProduct: React.FC<orderDetailProductProps> = observer(({ orderInfo, handleInitCartNum }) => {
  const { t } = useTranslation();
  const { orderStatusDTO, wareInfoDTOList, orderId, postCode, orderTagContentVO } = orderInfo || {};
  const { orderStatusId } = orderStatusDTO || {};
  const type = orderStatusId || "";

  // 埋点
  const commonTrackParams = { orderId: String(orderId), orderStatus: orderStatusId as "1" | "3" | "8" | "6" | "5" };

  const navigate = useNavigate();

  const [curSkuId, setCurSkuId] = useState("");

  const [showReturnProductModal, setShowReturnProductModal] = useState(false);
  const [showReturnRequestModal, setShowReturnRequestModal] = useState(false);
  const [showApplySuccessModal, setShowApplySuccessModal] = useState(false);
  const [showRecycleTipsModal, setShowRecycleTipsModal] = useState(false);
  const [afsServiceId, setAfsServiceId] = useState("");

  // 加入购物车
  const addCartHandler = (event: React.MouseEvent<HTMLButtonElement>, val: string) => {
    event.stopPropagation();
    // 埋点
    sendClickTrack.OrderDetail_AddtoCart({ ...commonTrackParams, skuid: val });
    addCartUtil([{ skuId: val, num: "1" }], handleInitCartNum);
  };

  // 跳转商详
  const jumpProductDetail = (item: DetailWareInfoListVo) => {
    jumpToDp(item?.wareId || "", item?.jumpParam);
  };

  const returnItemHandler = (event: React.MouseEvent<HTMLButtonElement>, val: string) => {
    event.stopPropagation();
    // 埋点
    sendClickTrack.OrderDetail_RefundItem({ ...commonTrackParams, skuid: val });
    setCurSkuId(val);
    setShowReturnProductModal(true);
  };

  if (!wareInfoDTOList?.length) {
    return null;
  }

  // 计算Order items
  const calcNum = (list: DetailWareInfoListVo[]) => {
    return list.reduce((sum: number, item) => sum + Number(item.buyCount), 0);
  };

  // view Detail 按钮点击刷新订详，关闭弹框
  const refreshDetailPage = () => {
    setShowApplySuccessModal(false);
    navigate(`/my-orders/refund?afsServiceId=${afsServiceId}&orderId=${orderId}`);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.productTitleWrap}>
        <div className={styles.product_title}>
          {t("order.orderDetail.orderDetailProduct.orderItemsText")}
          {wareInfoDTOList?.length ? `(${calcNum(wareInfoDTOList)})` : null}
        </div>
        {/* 大件回收标显示 */}
        {orderTagContentVO && orderTagContentVO?.tagSet?.includes("recycle") && (
          <div
            className={styles.recycleWrap}
            onClick={() => {
              setShowRecycleTipsModal(true);
            }}
          >
            {orderTagContentVO?.tagMainContentMap?.recycle?.title}
            <TipIcon className={styles.tipIco} />
          </div>
        )}
      </div>

      <ul className={styles.product_outer_wrap}>
        {wareInfoDTOList?.length
          ? wareInfoDTOList.map((item, index) => {
              const showOriginPrice = item.crossedUnitPrice && Number(item.crossedUnitPrice) > Number(item.jdPriceShow);
              return (
                <li
                  key={index}
                  className={cn(styles.product_wrap, {
                    [styles.product_special_wrap]: item.addCartShow || item.repairState === "1",
                  })}
                  onClick={() => jumpProductDetail(item)}
                >
                  <div className={styles.left_product}>
                    <div className={styles.product_info}>
                      <div className={styles.product_img}>
                        <UKImg
                          urlExtend={{
                            imgKey: item.imageShortUrl ? item.imageShortUrl : "",
                            width: 120,
                            height: 120,
                          }}
                        />
                      </div>
                      <div className={styles.product_main_info}>
                        <div className={styles.productName}>
                          <div className={styles.tagsWrap}>
                            <ProductTag tagList={item?.showTagList || []} />
                          </div>
                          <div className={styles.product_name_cont}>{item.wareName}</div>
                        </div>
                        <div className={styles.product_desc}>{item.propertyExt}</div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.right_product}>
                    <div className={styles.price_wrap}>
                      {/* 到手价 */}
                      <div className={styles.real_price}>
                        <JDIPrice
                          symbol={item.feeCurrencySymbol || ""}
                          price={item.jdPriceShow || ""}
                          uiType={1}
                          containerStyle={{
                            fontSize: "12px",
                            lineHeight: "16px",
                          }}
                          integerStyle={{
                            fontSize: "18px",
                            lineHeight: "24px",
                            height: "24px",
                          }}
                        />
                      </div>
                      {/* 划线价 */}
                      {/* 划线价与京东价一致则不展示 */}
                      {showOriginPrice ? (
                        <div className={styles.origin_price}>
                          <JDIPrice
                            symbol={item.feeCurrencySymbol || ""}
                            price={item.crossedUnitPrice || ""}
                            uiType={2}
                            containerStyle={{
                              fontSize: "12px",
                              lineHeight: "16px",
                            }}
                            lineThrough
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.product_num}>×{item.buyCount}</div>
                    {/* 操作按钮栏 */}
                    <div className={styles.btn_group}>
                      {item.repairState === "1" ? (
                        <CommonButton
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            returnItemHandler(e, item?.wareId || "");
                          }}
                          style={{
                            marginRight: "8px",
                          }}
                          exptag={"exp|" + EXPOSURE_RECORD.OrderDetail_RefundItemExpo}
                          data-exptag-config={JSON.stringify({
                            useClick: false,
                            stay_duration: 0.3,
                            repeated: false,
                            area_rate: 0.5,
                          })}
                          data-exptag-json-param={JSON.stringify({ ...commonTrackParams, skuid: item?.wareId || "" })}
                        >
                          {item.repairTitle}
                        </CommonButton>
                      ) : null}
                      {item.addCartShow && (
                        <CommonButton
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            addCartHandler(e, item.wareId || "");
                          }}
                          style={{
                            borderColor: "#cc0c1c",
                            color: "#cc0c1c",
                          }}
                          exptag={"exp|" + EXPOSURE_RECORD.OrderDetail_AddtoCartExpo}
                          data-exptag-config={JSON.stringify({
                            useClick: false,
                            stay_duration: 0.3,
                            repeated: false,
                            area_rate: 0.5,
                          })}
                          data-exptag-json-param={JSON.stringify({ ...commonTrackParams, skuid: item?.wareId || "" })}
                        >
                          {t("order.orderDetail.orderDetailProduct.addCartBtnText")}
                        </CommonButton>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          : null}
      </ul>
      {/* 售后商品选择弹窗 */}
      {showReturnProductModal && (
        <ReturnProductModal
          isOpen={showReturnProductModal}
          closeHandler={() => setShowReturnProductModal(false)}
          handleConfirmClick={() => {
            setShowReturnProductModal(false);
            setShowReturnRequestModal(true);
          }}
          orderId={orderId}
          skuId={Number(curSkuId)} // 将后端返回的字符串类型转化为数字
          skuList={wareInfoDTOList.map((item) => {
            return {
              skuId: item.wareId || "",
              skuUUid: item.skuUuid || "",
            };
          })}
        />
      )}
      {/* 售后申请表单弹窗 */}
      {showReturnRequestModal && (
        <ReturnRequestModal
          isOpen={showReturnRequestModal}
          closeHandler={() => setShowReturnRequestModal(false)}
          handleConfirmClick={(val) => {
            setShowReturnRequestModal(false);
            setShowApplySuccessModal(true);
            setAfsServiceId(val);
          }}
          orderId={orderId}
          postCode={postCode}
          skuIds={wareInfoDTOList.map((item) => item.wareId).join(",")}
        />
      )}

      {showApplySuccessModal && (
        <ApplySuccessModal isOpen={showApplySuccessModal} closeHandler={() => setShowApplySuccessModal(false)} viewDetailHandler={refreshDetailPage} />
      )}

      {/* 大件回收提示弹框 */}
      {showRecycleTipsModal && (
        <AgeTipsModal
          isOpen={showRecycleTipsModal}
          closeHandler={() => setShowRecycleTipsModal(false)}
          modalInfo={orderTagContentVO?.tagMainContentMap?.recycle}
        />
      )}
    </div>
  );
});

export default orderDetailProduct;
